import EventCalendarView from "./EventCalendarView.js";
import useAuth from "../auth/useAuth.js";
import PagePreview from "./PagePreview.js";
import Hijack from "../support/Hijack.js";

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Tabs, Tab, Typography, Grid, CircularProgress, Box, Button, Tooltip,
    Chip, Card, CardActionArea, CardMedia, CardContent, CardActions } from '@material-ui/core';
import { CalendarToday, Dashboard, Edit } from '@material-ui/icons';
import { Link as RouterLink, useLocation, useRouteMatch, useHistory} from "react-router-dom";
import Moment from 'react-moment';

function EventPagePreviewGrid({ loading, area, pages, applyDateFilter, onSubmitAttendance, toggleShowSorter, newAttendanceIds, tags }) {
    const auth = useAuth();
    let location = useLocation();
    let history = useHistory();
    let match = useRouteMatch();
    var query = new URLSearchParams(location.search);

    const [mode, setMode] = useState(query.get('tab') ?? 'list');
    const [canContribute, setCanContribute] = useState(false);
    const [canApprove, setCanApprove] = useState(false);

    useEffect(() => {
        if (auth?.user?.roles) {
            const roles = auth.user.roles;
            setCanContribute(roles.includes("events.approver"));
            setCanApprove(roles.includes("events.approver"));
        }
    }, [auth]);

    const handleChange = (e, v) => {
        setMode(v);
        toggleShowSorter(v !== "calendar");
        const newQuery = new URLSearchParams(location.search);
        newQuery.set('tab', v);
        history.replace(match.path + "?" + newQuery.toString());

        if (v !== "calendar") {
            applyDateFilter?.({ start: null, end: null });
        }
    };

    useEffect(() => toggleShowSorter(mode !== "calendar"), [mode]);

    return (
        <>
            <Tabs id="EventPagePreviewGrid" value={mode} onChange={handleChange} aria-label="event view" style={{ marginBottom: '16px' }}>
                <Tab icon={<CalendarToday />} label="Calendar" value="calendar" />
                <Tab icon={<Dashboard />} label="Upcoming Events" value="list" />
            </Tabs>

            {mode === "calendar" && <EventCalendarView area={area} tags={tags} applyDateFilter={applyDateFilter} pages={pages} canApprove={canApprove} canContribute={canContribute} />}
            {mode === "list" && <GenericPagePreviews loading={loading} area={area} pages={pages} tags={tags} canApprove={canApprove} canContribute={canContribute} /> }
        </>
    );
}

function GenericPagePreviews({ loading, area, pages, tags, canContribute, canApprove }) {
    return <Grid container spacing={3} justifyContent="space-between" alignItems="stretch">
        {pages.length > 0 ?
            pages.map((page) => <Grid key={page.id} item sm={6} xs={12}style={{display: "flex", flexDirection: "column"}}>
                    <EventPreview canApprove={canApprove} canContribute={canContribute} page={page} tags={tags} area={area} />
                </Grid>)
            :
            (loading ? <CircularProgress /> : <Typography>No events matching your criteria were found.</Typography>)
        }
    </Grid>
}

function MiniButton({ help, icon, link, style }) {
    return <Tooltip title={help}>
        <Button size="small" style={{...style, padding: '8px', margin: 0,  minWidth: 'auto', borderRadius: "100%" }} color="secondary" variant="contained" component={RouterLink} to={link}>{icon}</Button>
    </Tooltip>;
}

function EventPreview({ page, canContribute, embedded = false }){
    const auth = useAuth();
    const contentRef = useRef(null);

    const cardStyles = useMemo(() => {
        if(!embedded)
            return {height: "100%"};
        return {height: "100%", margin: 0, border: 0, boxShadow: 'none'};
    }, [embedded]);

    return <Card style={cardStyles}>
        <CardActionArea component={RouterLink} to={`/${page.area}/${page.url || page.id}`} style={{textDecoration: "none", height: "100%" }}>
            {page.thumbnail && <CardMedia component="img" height="140" image={`api/attachment/file/${page.image}/image`} alt={page.title} />}
            <CardContent>
                {page.state === "workflow" && <Chip label={"In Workflow"} variant="outlined" size="small" style={{position: "absolute", top: "0.5rem", right: "0.5rem" }} />}
                {canContribute && <MiniButton style={{position: "absolute", top: "0.5rem", left: "0.5rem"}} help="Make changes to the page" icon={<Edit />} link={`/${page.area}/${page.id}/edit`} />}
                <Typography gutterBottom variant="h4" component="div">{page.title}</Typography>

                 {page.eventDates && page.eventDates.length > 1 && <ul>
                     {page.eventDates.map((e,i) => <li key={i}>
                         {e.session}
                         <Moment format="DD/MM/YY hh.mm a">{e.eventStartDate}</Moment>
                         <br />
                         {e.presenter && `Presented by ${e.presenter}`}
                    </li>)}
                    </ul>}

                    {page.eventDates && page.eventDates.length === 1 && page.eventDates.map((e,i) => <Typography variant="h5" style={{padding: 0}} key={i}>
                         {e.session}
                         <Moment format="DD/MM/YY hh.mm a">{e.eventStartDate}</Moment>
                         <br />
                         {e.presenter && `Presented by ${e.presenter}`}
                    </Typography>)}

                    <Typography variant="body2" color="secondary">
                        <Hijack><Box ref={contentRef} dangerouslySetInnerHTML={{ __html: page?.description ?? "" }}></Box></Hijack>
                    </Typography>
            </CardContent>
        </CardActionArea>
    </Card>;
}

export { EventPagePreviewGrid, EventPreview };
